import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateUtility } from '@jms/model';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm/confirm.service';
import { DividerComponent } from '@workbuddy/ui/components/divider/divider.component';
import { TimeagoPipe } from '@workbuddy/ui/pipes/timeago.pipe';
import { ButtonIconComponent, ButtonAccent, ButtonDisplayStyle } from '@workbuddy/ui/v2/components/button';
import Enumerable from 'linq';
import type { AgentThread } from '../../agent.types';
import type { BackgroundThreadState } from '../../services/background.stream.manager';
import { TitleRevealDirective } from './title.reveal.directive';

@Component({
  selector: 'agent-thread-list',
  standalone: true,
  imports: [FormsModule, TimeagoPipe, ButtonIconComponent, DividerComponent, TitleRevealDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './thread.list.component.html',
  styleUrls: ['./thread.list.component.less'],
  host: {
    '[class]': '"mode-" + $mode()',
  },
})
export class AgentThreadListComponent {

  protected readonly ButtonAccent = ButtonAccent;
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;

  private readonly confirmService = inject(ConfirmServicePro);

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  public readonly $mode = input<'popup' | 'fullscreen'>('popup', { alias: 'mode' });
  public readonly $threads = input.required<AgentThread[]>({ alias: 'threads' });
  public readonly $activeThreadId = input<string | null>(null, { alias: 'activeThreadId' });
  public readonly $loading = input<boolean>(false, { alias: 'loading' });
  public readonly $threadStates = input<Map<string, BackgroundThreadState>>(new Map(), { alias: 'threadStates' });
  public readonly $showActiveIndicators = input<boolean>(false, { alias: 'showActiveIndicators' });

  // When the host drives search itself (e.g. the fullpage sidebar's "Search chats"
  // button), it passes the query in via `searchQuery` and hides the built-in search
  // box with `showInternalSearch=false`. Left null/true, the component keeps its own
  // self-contained search (used by the popup panel) exactly as before.
  public readonly $externalSearchQuery = input<string | null>(null, { alias: 'searchQuery' });
  public readonly $showInternalSearch = input<boolean>(true, { alias: 'showInternalSearch' });

  // ─── Outputs ────────────────────────────────────────────────────────────────

  public readonly threadSelected = output<{ threadId: string; title: string }>();
  public readonly threadDeleted = output<string>();

  // ─── Internal State ─────────────────────────────────────────────────────────

  protected readonly $searchQuery = signal('');
  private readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  // ─── Computed ───────────────────────────────────────────────────────────────

  protected readonly $showSearch = computed(() => this.$showInternalSearch() && this.$threads().length >= 10);

  // The query actually used for filtering: a host-supplied query takes precedence
  // over the built-in one, so the fullpage sidebar can own the search UI.
  protected readonly $effectiveQuery = computed(() => this.$externalSearchQuery() ?? this.$searchQuery());

  protected readonly $filteredThreads = computed(() => {

    const query = this.$effectiveQuery().toLowerCase().trim();
    const sorted = [...this.$threads()].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    if (!query) return sorted;
    return sorted.filter(t => (t.title || 'Untitled chat').toLowerCase().includes(query));
  });

  protected readonly $groupedThreads = computed(() => {

    const zone = DateUtility.getLocalTimeZone();
    const today = DateUtility.getLocalToday().startOf('day');
    const yesterday = today.minus({ days: 1 });
    const weekStart = today.startOf('week');
    const lastWeekStart = weekStart.minus({ weeks: 1 });
    const labelFor = (updatedAt: Date | string) => {
      const t = DateUtility.getDateTime(updatedAt, zone);
      if (t >= today) return 'Today';
      if (t >= yesterday) return 'Yesterday';
      if (t >= weekStart) return 'This week';
      if (t >= lastWeekStart) return 'Last week';
      return 'Older';
    };
    return Enumerable.from(this.$filteredThreads())
      .groupBy(thread => labelFor(thread.updatedAt))
      .select(g => ({ label: g.key(), threads: g.toArray() }))
      .toArray();
  });

  protected readonly $hasNoResults = computed(() => this.$filteredThreads().length === 0 && this.$effectiveQuery().trim().length > 0);

  protected readonly $isEmpty = computed(() => this.$threads().length === 0);

  protected readonly $indicatorMap = computed(() => {

    const states = this.$threadStates();
    const activeId = this.$activeThreadId();
    const showActive = this.$showActiveIndicators();
    const map = new Map<string, { shimmer: boolean; unread: boolean }>();
    for (const [id, state] of states) {
      const isActive = id === activeId && !showActive;
      map.set(id, {
        shimmer: state.streaming && !isActive,
        unread: state.unread && !isActive,
      });
    }
    return map;
  });

  // ─── Public API ──────────────────────────────────────────────────────────────

  public focusSearch(): void {

    this.searchInputRef()?.nativeElement.focus();
  }

  // ─── Actions ────────────────────────────────────────────────────────────────

  protected onThreadClick(thread: AgentThread): void {

    this.threadSelected.emit({ threadId: thread.id, title: thread.title ?? 'Untitled' });
  }

  protected async onDeleteClick(event: Event, thread: AgentThread): Promise<void> {

    event.stopPropagation();
    const confirmed = await this.confirmService.confirm('Delete conversation', 'Are you sure you want to delete this conversation?', 'danger', 'Delete', 'Cancel');
    if (confirmed) {
      this.threadDeleted.emit(thread.id);
    }
  }
}
